import React, { useState } from 'react';
import { Plus, User } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { useLifely } from '../context/LifelyContext';
import { CONTACT_CATEGORIES, CONTACT_ROLES } from '../store/mockData';
import { toast } from 'sonner';

const ContactSelector = ({
  value,
  onSelect,
  placeholder = 'Select contact...',
  categoryFilter = null,
  showAddNew = true,
  disabled = false,
  className = '',
}) => {
  const { contacts, addContact, getContactById } = useLifely();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  
  // New contact form state
  const [name, setName] = useState('');
  const [role, setRole] = useState('');
  const [category, setCategory] = useState(categoryFilter || '');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');

  // Filter contacts by category if specified
  const filteredContacts = categoryFilter
    ? contacts.filter(c => c.category === categoryFilter)
    : contacts;

  // Sort contacts alphabetically
  const sortedContacts = [...filteredContacts].sort((a, b) => 
    a.name.localeCompare(b.name)
  );

  // Get the selected contact details
  const selectedContact = value ? getContactById(value) : null;

  const resetForm = () => {
    setName('');
    setRole('');
    setCategory(categoryFilter || '');
    setEmail('');
    setPhone('');
    setNotes('');
  };

  const handleAddNew = () => {
    setIsAddDialogOpen(true);
  };

  const handleSaveNew = () => {
    if (!name.trim()) {
      toast.error('Name is required');
      return;
    }
    if (!category) {
      toast.error('Category is required');
      return;
    }

    const newContact = addContact({
      name: name.trim(),
      role,
      category,
      email: email.trim(),
      phone: phone.trim(),
      notes: notes.trim(),
    });

    toast.success('Contact added');
    setIsAddDialogOpen(false);
    resetForm();
    
    // Auto-select the newly created contact
    if (newContact && newContact.id) {
      onSelect(newContact.id);
    }
  };

  const handleSelect = (contactId) => {
    if (contactId === '__add_new__') {
      handleAddNew();
    } else if (contactId === '__clear__') {
      onSelect('');
    } else {
      onSelect(contactId);
    }
  };

  // Get roles filtered by selected category
  const filteredRoles = category
    ? CONTACT_ROLES.filter(r => r.category === category)
    : CONTACT_ROLES;

  return (
    <>
      <Select value={value || ''} onValueChange={handleSelect} disabled={disabled}>
        <SelectTrigger className={`h-9 ${className}`} data-testid="contact-selector">
          <SelectValue placeholder={placeholder}>
            {selectedContact ? selectedContact.name : placeholder}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {value && (
            <SelectItem value="__clear__" className="text-muted-foreground">
              Clear selection
            </SelectItem>
          )}
          {sortedContacts.length > 0 && (
            <>
              {sortedContacts.map((contact) => (
                <SelectItem key={contact.id} value={contact.id}>
                  <div className="flex items-center gap-2">
                    <User className="w-3 h-3 text-muted-foreground" />
                    <span>{contact.name}</span>
                    {contact.role && (
                      <span className="text-xs text-muted-foreground">
                        ({CONTACT_ROLES.find(r => r.value === contact.role)?.label || contact.role})
                      </span>
                    )}
                  </div>
                </SelectItem>
              ))}
            </>
          )}
          {showAddNew && (
            <SelectItem value="__add_new__" className="text-primary font-medium">
              <div className="flex items-center gap-2">
                <Plus className="w-3 h-3" />
                <span>Add new contact</span>
              </div>
            </SelectItem>
          )}
        </SelectContent>
      </Select>

      {/* Add New Contact Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-heading">Add New Contact</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Name *</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Full name or organization"
                className="h-9"
                data-testid="new-contact-name"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Category *</Label>
                <Select value={category} onValueChange={(v) => { setCategory(v); setRole(''); }}>
                  <SelectTrigger className="h-9" data-testid="new-contact-category">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {CONTACT_CATEGORIES.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Role / Relationship</Label>
                <Select value={role} onValueChange={setRole}>
                  <SelectTrigger className="h-9" data-testid="new-contact-role">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredRoles.map((r) => (
                      <SelectItem key={r.value} value={r.value}>
                        {r.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Email</Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="email@example.com"
                  className="h-9"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Phone</Label>
                <Input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+1 555 123 4567"
                  className="h-9"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Notes</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any additional information..."
                className="min-h-[60px] resize-none text-sm"
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setIsAddDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveNew} data-testid="save-new-contact-btn">
              Add Contact
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ContactSelector;
